import { InfoCircleOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Divider,
  Flex,
  Input,
  Popover,
  Select,
  Space,
  Tabs,
  TabsProps,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../../../../api";
import Colors from "../../../../../assets/styles/Colors";
import Loader from "../../../../../components/Loader";
import { IntTplProduct } from "../../../../../domain/types";
import { useAppSelector } from "../../../../../store";
import { AppState } from "../../../../../store/appSlice";
import AdminLangSwicher from "../../../../admin_components/AdminLangSwicher";
import AdminInternationalTplPackageEdit from "./AdminInternationalTplPackageEdit";
import React from "react";
const { Text } = Typography;

const AdminInternationalTplProductEdit = () => {
  const { productId: str = "0" } = useParams();
  const productId = parseInt(str);
  const { field_Language } = useAppSelector(
    (data: { app: AppState }) => data.app,
  );
  const { t } = useTranslation();

  const [product, setProduct] = useState<Partial<IntTplProduct> | null>(null);

  const {
    data: productData,
    isLoading: isProductDataLoading,
    refetch: refetchProductData,
  } = api.useGetIntTplProductQuery(productId, {
    skip: !productId,
  });

  const [productUpdate, { isLoading: isProductUpdateLoading }] =
    api.useUpdateIntTplProductMutation();

  const actualProductId = productData?.mainProduct?.id || productId;
  const { data: packages, refetch: refetchPackages } =
    api.useGetIntTplPackagesQuery(actualProductId, {
      skip: !actualProductId,
    });

  const _updateProduct = async () => {
    try {
      await productUpdate({
        ...product,
        mainProduct: {
          ...product?.mainProduct,
          status: "draft",
        } as IntTplProduct["mainProduct"],
      }).unwrap();

      refetchProductData();
      toast.success(`${t("product")} ${t("updatedSuccessfully")}`);
    } catch (error) {
      if (error?.data?.errors?.constructor === Array) {
        const firstError = error.data.errors[0];
        const errorMessage = firstError.message;
        toast.error(errorMessage);
      } else {
        toast.error(`${t("errorOccurred")}`);
      }
    }
  };

  const _updateProductStatus = async (status: "published" | "draft") => {
    try {
      await productUpdate({
        ...product,
        mainProduct: {
          ...product?.mainProduct,
          status,
        } as IntTplProduct["mainProduct"],
      }).unwrap();

      refetchProductData();
      toast.success(`${t("status")} ${t("updatedSuccessfully")}`);
    } catch (error) {
      if (error?.data?.errors?.constructor === Array) {
        const firstError = error.data.errors[0];
        const errorMessage = firstError.message;
        toast.error(errorMessage);
      } else {
        toast.error(`${t("errorOccurred")}`);
      }
    }
  };

  useEffect(() => {
    if (productData) {
      setProduct(productData);
      // Refetch packages when product data loads
      if (productData?.mainProduct?.id) {
        refetchPackages();
      }
    }

    return () => {};
  }, [productData, refetchPackages]);

  const setProductField = (
    field: keyof IntTplProduct["mainProduct"],
    value: any,
  ) => {
    setProduct({
      ...product,
      mainProduct: {
        ...product?.mainProduct,
        [field]: value,
      } as IntTplProduct["mainProduct"],
    });
  };

  // #######################################################
  const items: TabsProps["items"] = [
    {
      key: "1",
      label: t("product"),
      children:
        isProductDataLoading || isProductUpdateLoading ? (
          <Loader style={{ marginTop: 250 }} />
        ) : (
          <>
            <Flex vertical gap={10}>
              <Flex gap={20} align="flex-end">
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Text>{t("title")}</Text>
                  <Input
                    size="large"
                    placeholder=""
                    value={
                      product?.mainProduct?.translations?.[field_Language]
                        ?.title
                    }
                    onChange={(e) => {
                      setProductField("translations", {
                        ...product?.mainProduct?.translations,
                        [field_Language]: {
                          ...product?.mainProduct?.translations?.[
                            field_Language
                          ],
                          title: e?.target?.value,
                        },
                      });
                    }}
                    width={"fullWidth"}
                  />
                </Space>

                <Space direction="vertical" style={{ width: "100%" }}>
                  <Text>{t("insuranceCompany")}</Text>
                  <Input
                    disabled
                    size="large"
                    placeholder=""
                    value={
                      product?.mainProduct?.company?.brandName?.[field_Language]
                        ?.title || ""
                    }
                    width={"fullWidth"}
                  />
                </Space>
              </Flex>

              <Flex gap={20} align="flex-end">
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Text>{t("productGroup")}</Text>
                  <Input
                    disabled
                    size="large"
                    placeholder=""
                    value={`${t("internationalTplGroup")}`}
                    width={"fullWidth"}
                  />
                </Space>

                <Space direction="vertical" style={{ width: "100%" }}>
                  <Text>{t("product")}</Text>
                  <Input
                    disabled
                    size="large"
                    placeholder=""
                    value={`${t("internationalTpl")}`}
                    width={"fullWidth"}
                  />
                </Space>
              </Flex>

              <Divider
                variant="dashed"
                style={{
                  borderColor: Colors.blue,
                }}
              />

              <Flex gap={20} align="flex-end">
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Text>{t("status")}</Text>
                  <Select
                    style={{ width: "100%" }}
                    placeholder={t("choosePolicyStatus")}
                    size="large"
                    value={product?.mainProduct?.integrated}
                    onChange={(value) => setProductField("integrated", value)}
                    options={[
                      ...[
                        {
                          status: true,
                          label: t("integrated"),
                        },
                        {
                          status: false,
                          label: t("nonIntegrated"),
                        },
                      ].map((item) => ({
                        value: item.status,
                        label: item.label,
                      })),
                    ]}
                  />
                </Space>

                <Space direction="vertical" style={{ width: "100%" }}>
                  <Text>{t("productMarketingPrice")}</Text>
                  <Input
                    type="number"
                    size="large"
                    placeholder=""
                    value={product?.marketingPrice}
                    onChange={(e) =>
                      setProduct({
                        ...product,
                        marketingPrice: Number(e?.target?.value),
                      })
                    }
                    width={"fullWidth"}
                  />
                </Space>
              </Flex>

              <Flex align="flex-end" gap={20}>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Text>
                    {t("startDateRestriction")}
                    <Tooltip title={t("startDateRestrictionTooltip")}>
                      <InfoCircleOutlined style={{ marginLeft: 5 }} />
                    </Tooltip>
                  </Text>

                  <Input
                    type="number"
                    size="large"
                    placeholder=""
                    value={product?.limitationOfStartDate}
                    onChange={(e) =>
                      setProduct({
                        ...product,
                        limitationOfStartDate: Number(e?.target?.value),
                      })
                    }
                    width={"fullWidth"}
                  />
                </Space>

                <Space direction="vertical" style={{ width: "100%" }}>
                  <Text>
                    {t("productRealizationDate")}{" "}
                    <Tooltip title={t("productRealizationDateTooltip")}>
                      <InfoCircleOutlined style={{ marginLeft: 5 }} />
                    </Tooltip>
                  </Text>
                  <Input
                    type="number"
                    size="large"
                    placeholder=""
                    value={product?.limitBeforeRealization}
                    onChange={(e) =>
                      setProduct({
                        ...product,
                        limitBeforeRealization: Number(e?.target?.value),
                      })
                    }
                    width={"fullWidth"}
                  />
                </Space>

                <Space direction="vertical" style={{ width: "100%" }}>
                  <Text>{t("productFee")}</Text>
                  <Input
                    type="number"
                    size="large"
                    placeholder=""
                    value={product?.mainProduct?.commission}
                    onChange={(e) =>
                      setProductField("commission", e?.target?.value)
                    }
                    width={"fullWidth"}
                  />
                </Space>
              </Flex>

              <Divider
                variant="dashed"
                style={{
                  borderColor: Colors.blue,
                }}
              />

              <Card type="inner" hoverable size="small">
                <Flex wrap gap={20} justify="space-around" align="center">
                  <Tag color="blue">
                    <Space>
                      <Text>{t("createdDate")}:</Text>
                      <Text>
                        {dayjs(product?.mainProduct?.createdAt).format(
                          "D / MM / YYYY | HH:mm:ss",
                        )}
                      </Text>
                    </Space>
                  </Tag>
                  <Tag color="geekblue">
                    <Space>
                      <Text>{t("lastUpdatedAt")}:</Text>
                      <Text>
                        {dayjs(product?.mainProduct?.updatedAt).format(
                          "D / MM / YYYY | HH:mm:ss",
                        )}
                      </Text>
                    </Space>
                  </Tag>
                  <Tag color="blue">
                    <Space>
                      <Text>{t("status")}:</Text>
                      <Text
                        style={{
                          letterSpacing: 2,
                          textTransform: "uppercase",
                          fontWeight: "bold",
                          color:
                            product?.mainProduct?.status === "published"
                              ? "inherit"
                              : Colors.primaryRed,
                        }}
                      >
                        {product?.mainProduct?.status}
                      </Text>
                    </Space>
                  </Tag>
                </Flex>
              </Card>

              <Divider
                variant="dashed"
                style={{
                  borderColor: Colors.blue,
                }}
              />

              <Flex gap={20}>
                <Button
                  type="primary"
                  size="large"
                  onClick={_updateProduct}
                  style={{ width: "100%" }}
                >
                  {t("save")}
                </Button>

                {(() => {
                  const button = (
                    <Button
                      type="default"
                      size="large"
                      onClick={() => {
                        if (product?.mainProduct?.status === "published") {
                          _updateProductStatus("draft");
                        } else if (product?.mainProduct?.status === "draft") {
                          _updateProductStatus("published");
                        } else {
                          toast.info(
                            `${t("unknownError")} _updateProductStatus()`,
                          );
                        }
                      }}
                      style={{ width: "100%" }}
                    >
                      {product?.mainProduct?.status === "published"
                        ? t("moveToDraft")
                        : t("publish")}
                    </Button>
                  );

                  return button;
                })()}
              </Flex>
            </Flex>
          </>
        ),
    },
    {
      key: "2",
      label: t("packages"),
      children: (
        <AdminInternationalTplPackageEdit
          productId={productData?.mainProduct?.id}
          onPackageUpdate={refetchPackages}
        />
      ),
    },
  ];

  const handleTabChange = (activeKey: string) => {
    // Refetch packages when switching to product edit tab (tab "1")
    if (activeKey === "1" && actualProductId) {
      refetchPackages();
    }
  };

  return (
    <div>
      <Tabs
        animated
        type="card"
        tabBarExtraContent={<AdminLangSwicher />}
        size="middle"
        defaultActiveKey="1"
        items={items}
        onChange={handleTabChange}
      />
    </div>
  );
};

export default AdminInternationalTplProductEdit;
